/**
 * A display-only tile basemap (Esri World Imagery) drawn by the browser: the same 1280 px picture the server's
 * basemap geometry describes (app/design/basemap/geometry.py: centred on the pin, Web Mercator zoom, scale 2), so
 * the design's `enu_to_px` holds unchanged. Tiles are fetched live with the browser key and painted on a canvas;
 * the picture lives only in this tab (an object URL) and is never uploaded, stored or analysed.
 */
const TILE_PX = 256;
export const IMAGE_PX = 1280;

/** Global pixel coordinates (256-pixel tiles) of a point at `zoom`. */
export function worldPx(lat: number, lng: number, zoom: number): [number, number] {
  const size = TILE_PX * 2 ** zoom;
  const clamped = Math.max(-85.05112878, Math.min(85.05112878, lat));
  const s = Math.sin((clamped * Math.PI) / 180);
  return [((lng + 180) / 360) * size, (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * size];
}

export interface TilePlan {
  tileZoom: number;
  /** Top-left of the picture in global pixels at `tileZoom`, and the global pixels one picture pixel spans. */
  left: number;
  top: number;
  span: number;
  tiles: { x: number; y: number }[];
}

/** Which tiles make the picture, and where (as the server's `tile_window`). */
export function tilePlan(lat: number, lng: number, zoom: number, maxZoom: number, size = IMAGE_PX, scale = 2): TilePlan {
  const tileZoom = Math.min(zoom + 1, maxZoom);
  const span = 2 ** (tileZoom - zoom) / scale;
  const [cx, cy] = worldPx(lat, lng, tileZoom);
  const left = cx - (size / 2) * span;
  const top = cy - (size / 2) * span;
  const x0 = Math.floor(left / TILE_PX);
  const y0 = Math.floor(top / TILE_PX);
  const x1 = Math.floor((left + size * span) / TILE_PX);
  const y1 = Math.floor((top + size * span) / TILE_PX);
  const tiles = [];
  for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) tiles.push({ x, y });
  return { tileZoom, left, top, span, tiles };
}

export function tileUrl(template: string, key: string, z: number, x: number, y: number): string {
  const n = 2 ** z;
  return template.replace("{z}", String(z)).replace("{x}", String(((x % n) + n) % n)).replace("{y}", String(y)).replace("{key}", encodeURIComponent(key));
}

/** Paints the picture from live tiles; resolves to an object URL (the caller revokes it). */
export async function drawTiles(template: string, key: string, lat: number, lng: number, zoom: number, maxZoom: number): Promise<string> {
  const plan = tilePlan(lat, lng, zoom, maxZoom);
  const canvas = document.createElement("canvas");
  canvas.width = IMAGE_PX;
  canvas.height = IMAGE_PX;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = "#c8c8c8";
  ctx.fillRect(0, 0, IMAGE_PX, IMAGE_PX);
  const k = 1 / plan.span; // picture pixels per tile pixel
  await Promise.all(
    plan.tiles.map(
      (t) =>
        new Promise<void>((resolve) => {
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = () => {
            ctx.drawImage(img, (t.x * TILE_PX - plan.left) * k, (t.y * TILE_PX - plan.top) * k, TILE_PX * k, TILE_PX * k);
            resolve();
          };
          img.onerror = () => resolve();
          img.src = tileUrl(template, key, plan.tileZoom, t.x, t.y);
        }),
    ),
  );
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.9));
  if (!blob) throw new Error("The basemap could not be drawn");
  return URL.createObjectURL(blob);
}
